/**
 * Pallas AI Analyst Service
 *
 * MCP (Model Context Protocol) client + WebSocket factory for the Pallas server.
 *
 * Auth: requests forward the logged-in user's Keycloak token. Trusted embeds
 * may supply a real user token through the origin-checked parent bridge. No
 * reusable service credential is compiled into or sent by the browser.
 */

import keycloak from '../config/keycloak';
import { shouldRetryWithParentToken } from './pallasAuthRetry';

const PALLAS_BASE_URL = process.env.REACT_APP_PALLAS_URL || '/ai-analyst';
const PALLAS_WS_URL = process.env.REACT_APP_PALLAS_WS_URL || '';

/**
 * Return the current Keycloak JWT for standalone SSO mode.
 * Falls back to a stored token while the Keycloak adapter initializes.
 *
 * ReactKeycloakProvider owns the token lifecycle. Calling updateToken() from
 * every startup API request can make keycloak-js clear the in-memory token and
 * invoke login-required again if refresh fails, producing a full-page loop.
 */
function getKeycloakToken() {
  if (keycloak && keycloak.authenticated && keycloak.token) {
    return keycloak.token;
  }
  try {
    return sessionStorage.getItem('keycloak_token') || localStorage.getItem('keycloak_token') || null;
  } catch {
    return null;
  }
}

/**
 * The bearer VALUE (no "Bearer " prefix) this client would authenticate with,
 * or null. Same precedence as authHeaders(): parent-frame token first, then the
 * standalone Keycloak JWT.
 *
 * Exported for the chat WebSocket, which cannot send an Authorization header on
 * a handshake and instead passes the token in an "auth" control frame so
 * response-plane tools can read Ageleia as this analyst.
 */
export function getAuthToken() {
  const parentAuth = (typeof window !== 'undefined') ? window.__pallasParentAuth : null;
  if (parentAuth && parentAuth.token) return parentAuth.token;
  return getKeycloakToken();
}

/**
 * True when a JWT expires within `seconds` (default 60), so the caller can
 * refresh before using it somewhere that yields no 401 to retry on.
 *
 * Fail-soft by design: an unreadable or unparseable token returns false —
 * "probably fine, try it" — because the server validates it anyway and a bad
 * guess here would cost a needless refresh round-trip on every query.
 */
export function tokenExpiresSoon(token, seconds = 60) {
  try {
    const payload = JSON.parse(atob(String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload || typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 - Date.now() < seconds * 1000;
  } catch (_) {
    return false;
  }
}

async function authHeaders() {
  const headers = {};
  // 1) Parent-frame (iframe-bridge) auth — the Wazuh dashboard plugin / Ageleia
  //    embed may post a real user token via `pallas-auth-token`; stored in
  //    window.__pallasParentAuth. A username by itself is display data only and
  //    is never sent as authentication.
  const parentAuth = (typeof window !== 'undefined') ? window.__pallasParentAuth : null;
  if (parentAuth && parentAuth.token) {
    headers['Authorization'] = `Bearer ${parentAuth.token}`;
    return headers;
  }
  // 2) Standalone Keycloak SSO — forward the current JWT as the bearer.
  const jwt = getKeycloakToken();
  if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }
  return headers;
}

/**
 * Return the current analyst's display name for UI labels.
 *
 * Reads `preferred_username` from the parsed Keycloak token (the standard
 * OIDC claim). Falls back to `name`, `email`, or 'SOC Analyst' literal as
 * a last resort if Keycloak is not initialized yet. The backend ignores this
 * value for authorization and audit attribution, using its verified token
 * principal instead.
 */
export function getCurrentAnalystName() {
  try {
    // Parent-frame mode: prefer the username posted by the parent (Wazuh plugin).
    if (typeof window !== 'undefined' && window.__pallasParentAuth) {
      const p = window.__pallasParentAuth;
      if (p.username) return p.username;
      if (p.displayName) return p.displayName;
    }
    // Standalone Keycloak mode: read the OIDC claim from the parsed token.
    if (keycloak && keycloak.tokenParsed) {
      const t = keycloak.tokenParsed;
      return t.preferred_username || t.name || t.email || 'SOC Analyst';
    }
  } catch (_) {
    // Fall through to default
  }
  return 'SOC Analyst';
}

/**
 * fetch() wrapper that:
 *   1. attaches the current Keycloak JWT
 *   2. in iframe mode only, asks the trusted parent for a fresh token and
 *      retries once after a 401
 *   3. honors an optional AbortSignal for caller-controlled timeouts
 * Returns the Response object — caller decides how to interpret.
 */
// Request a fresh token from the parent frame (iframe-mode only). Resolves to
// true once a new token arrives within `timeoutMs`, false on timeout. The
// parent listens for { type: 'pallas-iframe-needs-token' } and re-posts.
export async function requestFreshTokenFromParent(timeoutMs = 5000) {
  if (typeof window === 'undefined' || !window.__pallasParentAuth) return false;
  const beforeReceivedAt = window.__pallasParentAuth.receivedAt || 0;
  try {
    window.parent.postMessage({ type: 'pallas-iframe-needs-token' }, '*');
  } catch (_) {
    return false;
  }
  return new Promise((resolve) => {
    const start = Date.now();
    const interval = setInterval(() => {
      const cur = (window.__pallasParentAuth && window.__pallasParentAuth.receivedAt) || 0;
      if (cur > beforeReceivedAt) {
        clearInterval(interval);
        resolve(true);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(interval);
        resolve(false);
      }
    }, 100);
  });
}

async function authedFetch(url, options = {}) {
  const buildOptions = async () => ({
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(await authHeaders()),
    },
  });

  let response = await fetch(url, await buildOptions());

  // In standalone mode ReactKeycloakProvider owns token refresh. Do not invoke
  // Keycloak refresh from the request path merely because the backend returned
  // 401: a 401 may come from token validation or a downstream service,
  // and keycloak-js clears the SSO token and starts login-required again when
  // refresh fails. That was the source of the endless redirect/reload loop.
  //
  // Iframe mode is different: the parent owns the user token, so it is safe to
  // ask that parent for a newer token and retry exactly once.
  const parentAuth = typeof window !== 'undefined' ? window.__pallasParentAuth : null;
  if (shouldRetryWithParentToken(response.status, parentAuth)) {
    const got = await requestFreshTokenFromParent();
    if (got) {
      response = await fetch(url, await buildOptions());
    }
  } else if (response.status === 401) {
    console.warn('[Pallas] Backend returned 401; keeping the standalone SSO session intact.');
  }

  return response;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export const PALLAS_CONFIG = {
  get baseUrl() {
    // If absolute URL provided, use as-is; otherwise resolve against origin
    if (PALLAS_BASE_URL.startsWith('http')) return PALLAS_BASE_URL;
    return `${window.location.origin}${PALLAS_BASE_URL}`;
  },
  get wsUrl() {
    // Explicit WS URL takes precedence (supports reverse proxy configs)
    if (PALLAS_WS_URL) {
      const wsBase = PALLAS_WS_URL.replace(/\/$/, '');
      return `${wsBase}/ws/chat`;
    }
    // Derive from base URL — replace http(s) with ws(s)
    const base = this.baseUrl;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const hostPath = base.replace(/^https?:\/\//, '');
    return `${protocol}//${hostPath}/ws/chat`;
  },
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

let _requestId = 0;
const nextId = () => ++_requestId;

async function mcpRequest(method, params = {}) {
  const url = `${PALLAS_CONFIG.baseUrl}/`;
  const body = {
    jsonrpc: '2.0',
    method,
    params,
    id: nextId(),
  };

  const response = await authedFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Pallas request was unauthorized. Please contact your Athena administrator.');
    }
    throw new Error(`MCP HTTP error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  if (data.error) {
    throw new Error(data.error.message || 'MCP error');
  }

  return data.result;
}

// ---------------------------------------------------------------------------
// MCP Protocol Methods
// ---------------------------------------------------------------------------

/**
 * Initialize MCP session — returns server info + capabilities
 */
export async function mcpInitialize() {
  return mcpRequest('initialize', {
    protocolVersion: '2025-06-18',
    capabilities: {},
    clientInfo: { name: 'athena-pallas', version: '1.0.0' },
  });
}

/**
 * List all available MCP tools
 */
export async function mcpListTools() {
  const result = await mcpRequest('tools/list', {});
  return result?.tools || [];
}

/**
 * Call an MCP tool and return the parsed result
 * @param {string} toolName - Tool name (e.g. 'get_wazuh_agents')
 * @param {object} args - Tool arguments
 * @returns {{ text: string, isError: boolean }}
 */
export async function mcpCall(toolName, args = {}) {
  const result = await mcpRequest('tools/call', {
    name: toolName,
    arguments: args,
  });

  const content = result?.content?.[0];
  return {
    text: content?.text || '',
    isError: result?.isError || false,
  };
}

// ---------------------------------------------------------------------------
// REST Endpoints (non-MCP)
// ---------------------------------------------------------------------------

/**
 * Fetch server health status
 */
export async function fetchHealth() {
  const response = await authedFetch(`${PALLAS_CONFIG.baseUrl}/health`);
  if (!response.ok) throw new Error(`Health check failed: ${response.status}`);
  return response.json();
}

/**
 * Fetch this analyst's last N most-recently-active conversation threads.
 * Returns the raw {threads, tenant, backend, limit} envelope from /api/threads.
 * Throws on non-OK so the hook can decide between empty-list and offline-fallback.
 */
export async function fetchThreads(limit = 15) {
  const response = await authedFetch(
    `${PALLAS_CONFIG.baseUrl}/api/threads?limit=${encodeURIComponent(limit)}`
  );
  if (!response.ok) throw new Error(`fetchThreads HTTP ${response.status}`);
  return response.json();
}

/**
 * Fetch the stored turns for one of this analyst's conversations.
 * Returns summary-level turns (query, summary, tool_used, timestamp). The full
 * ResponsePanel blocks are not server-stored — see the local cache for those.
 */
export async function fetchThreadTurns(conversationId) {
  const response = await authedFetch(
    `${PALLAS_CONFIG.baseUrl}/api/threads/${encodeURIComponent(conversationId)}/turns`
  );
  if (!response.ok) throw new Error(`fetchThreadTurns HTTP ${response.status}`);
  return response.json();
}

/**
 * Delete a thread by conversation_id (server-side, all turns).
 * Resolves with {conversation_id, deleted_turns}.
 */
export async function deleteThreadOnServer(conversationId) {
  const response = await authedFetch(
    `${PALLAS_CONFIG.baseUrl}/api/threads/${encodeURIComponent(conversationId)}`,
    { method: 'DELETE' }
  );
  if (!response.ok) throw new Error(`deleteThread HTTP ${response.status}`);
  return response.json();
}

/**
 * Rename a thread. Stored with the chat on the server, so the name survives a
 * reload and shows on every device. Resolves with {conversation_id, title}.
 */
export async function renameThreadOnServer(conversationId, title) {
  const response = await authedFetch(
    `${PALLAS_CONFIG.baseUrl}/api/threads/${encodeURIComponent(conversationId)}/title`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    }
  );
  if (!response.ok) throw new Error(`renameThread HTTP ${response.status}`);
  return response.json();
}

/**
 * Fetch server meta — tool health, feature flags.
 * Used by workspace layout for NavigationRail + StatusBar health dots.
 *
 * The Pallas server exposes /health (not /meta). We fetch /health and
 * map its `services` object into the shape the frontend expects:
 *   { cti_enabled, ollama_enabled, services }
 *
 * @returns {{ cti_enabled: boolean, ollama_enabled: boolean, services: object }}
 */
export async function fetchMeta() {
  const response = await authedFetch(`${PALLAS_CONFIG.baseUrl}/health`);
  if (!response.ok) throw new Error(`Meta fetch failed: ${response.status}`);
  const data = await response.json();

  // Map /health services into the meta shape the workspace layout expects
  const svc = data.services || {};
  return {
    cti_enabled: svc.suricata_ids === 'healthy',
    ollama_enabled: Boolean(svc.query_orchestrator && svc.query_orchestrator !== 'unhealthy'),
    services: svc,
    version: data.version,
    status: data.status,
  };
}

/**
 * Fetch the total open-vulnerability count for the sidebar badge.
 * Best-effort: returns null on any failure so the badge simply hides.
 *
 * @returns {Promise<number|null>}
 */
export async function fetchVulnsCount() {
  try {
    const response = await authedFetch(`${PALLAS_CONFIG.baseUrl}/api/vulns?limit=1`);
    if (!response.ok) return null;
    const data = await response.json();
    const total = typeof data.total === 'number' ? data.total : null;
    return total;
  } catch (_) {
    return null;
  }
}

/**
 * Authenticated POST to a Pallas REST endpoint.
 * Used by AttackNarrative, DecoderLab, DetectIOC for non-MCP endpoints.
 *
 * In standalone mode a backend 401 is surfaced to the UI; the request path
 * never refreshes Keycloak because not every backend 401 means that the user's
 * JWT expired.
 *
 * Optionally accepts a `timeoutMs` so callers (e.g. narrative generation,
 * which legitimately runs up to 180s on the backend) can pick an explicit
 * client-side timeout that matches the backend rather than relying on the
 * browser's default ~30-90s. Throws an `AbortError` (`err.name === 'AbortError'`)
 * on timeout so the caller can render a tailored error.
 *
 * With `structuredError: true` the thrown Error also carries `err.status`
 * and `err.body` (the response JSON when it parses, else the raw text) so an
 * action handler can turn an Ageleia/Pallas refusal into a readable sentence
 * (see PallasActionOutcome.js). The thrown message is unchanged either way,
 * so existing callers see exactly what they always did.
 *
 * @param {string} path - Endpoint path (e.g. '/api/narrative/enhance')
 * @param {object} body - JSON body
 * @param {{ timeoutMs?: number, structuredError?: boolean }} [opts]
 * @returns {Promise<any>} Parsed JSON response
 */
// Fields that quote raw alert content (the alert itself, a map of alerts, the
// narrative text). The test server's WAF refused request bodies carrying exploit
// strings taken from the alerts ("../", "/etc/passwd", "<script>", "${jndi:"),
// so Acknowledge and Create ticket failed on exactly the alerts that matter.
// They travel encoded: <field>_gz (gzip, then base64) where the browser can
// compress, else <field>_b64. The backend decodes both
// (server._unpack_b64_fields) and still accepts the plain field.
const B64_FIELDS = ['alert', 'alerts', 'narrative'];

// The same WAF (AWS managed Core rule set, SizeRestrictions_BODY) refuses any
// request body over 8 KB with a bare "403 Forbidden" page. A narrative alone is
// about 6 KB, so Create ticket, bulk Acknowledge and large alerts hit it. Bodies
// that carry alerts or a narrative are fitted under this budget (8,192 less a
// margin) by fitBodyToWafBudget / splitAlertsForWafBudget before they are sent.
export const WAF_BODY_BUDGET = 7600;
// The backend refuses a compressed field that expands past 256 KB
// (server._GZ_FIELD_MAX_BYTES), so each field is also kept under this size.
export const WAF_FIELD_RAW_MAX = 200 * 1024;

export function packForTransport(body) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return body;
  let out = null;
  for (const key of B64_FIELDS) {
    if (body[key] === undefined || body[key] === null) continue;
    try {
      const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(body[key]))));
      out = out || { ...body };
      out[`${key}_b64`] = encoded;
      delete out[key];
    } catch (_e) { /* leave the plain field in place */ }
  }
  return out || body;
}

// UTF-8 bytes of a string (the test environment has no TextEncoder).
function utf8Bytes(text) {
  const binary = unescape(encodeURIComponent(text));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function bytesToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

async function gzipBase64(text) {
  const stream = new CompressionStream('gzip');
  const writer = stream.writable.getWriter();
  // Not awaited: the readable side must be drained while the writer runs.
  writer.write(utf8Bytes(text)).catch(() => {});
  writer.close().catch(() => {});
  const reader = stream.readable.getReader();
  const parts = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value);
    total += value.length;
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) { out.set(part, offset); offset += part.length; }
  return bytesToBase64(out);
}

/** packForTransport, with each field gzip-compressed (<field>_gz) when the
 * browser has CompressionStream; any failure falls back to <field>_b64. */
export async function packForTransportCompressed(body) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return body;
  if (typeof CompressionStream !== 'function') return packForTransport(body);
  try {
    let out = null;
    for (const key of B64_FIELDS) {
      if (body[key] === undefined || body[key] === null) continue;
      const encoded = await gzipBase64(JSON.stringify(body[key]));
      out = out || { ...body };
      out[`${key}_gz`] = encoded;
      delete out[key];
    }
    return out || body;
  } catch (_e) {
    return packForTransport(body);
  }
}

/** Bytes the body will occupy on the wire, as pallasPost sends it. */
export async function wafBodyBytes(body) {
  return utf8Bytes(JSON.stringify(await packForTransportCompressed(body))).length;
}

/** True when the body passes the WAF: its encoded size is within the budget and
 * no encoded field expands past WAF_FIELD_RAW_MAX (the server refuses those). */
export async function fitsWafBudget(body) {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    for (const key of B64_FIELDS) {
      const value = body[key];
      if (value === undefined || value === null) continue;
      if (unescape(encodeURIComponent(JSON.stringify(value))).length > WAF_FIELD_RAW_MAX) return false;
    }
  }
  return (await wafBodyBytes(body)) <= WAF_BODY_BUDGET;
}

const SHORTENED = 'shortened to pass the WAF size limit';
const TRUNCATED_MARK = ` … [${SHORTENED}]`;
// An alert is shortened in these steps: text values cut to maxLen, lists to maxItems.
const SHRINK_STEPS = [
  { maxLen: 2000, maxItems: 50 },
  { maxLen: 800, maxItems: 20 },
  { maxLen: 300, maxItems: 8 },
];
const NARRATIVE_STEPS = [16000, 12000, 9000, 7000, 5000, 3500, 2500, 1500];
const NARRATIVE_MARK = `\n\n… [The narrative was ${SHORTENED}; the full text is in Pallas.]`;

// A copy of the value with every string longer than maxLen cut to maxLen and
// every list cut to maxItems; a list of strings says how many it left out.
// Keys, numbers and short strings (ids, rule, agent, IPs, severity) are kept.
function shortenLongValues(value, maxLen, maxItems, depth = 0) {
  if (typeof value === 'string') return value.length > maxLen ? value.slice(0, maxLen) + TRUNCATED_MARK : value;
  if (value === null || typeof value !== 'object') return value;
  if (depth > 20) return `[nested data ${SHORTENED}]`;
  if (Array.isArray(value)) {
    const kept = value.slice(0, maxItems).map((v) => shortenLongValues(v, maxLen, maxItems, depth + 1));
    if (value.length > maxItems && kept.every((v) => typeof v === 'string')) {
      kept.push(`… ${value.length - maxItems} more ${SHORTENED}`);
    }
    return kept;
  }
  const out = {};
  for (const [k, v] of Object.entries(value)) out[k] = shortenLongValues(v, maxLen, maxItems, depth + 1);
  return out;
}

// The fields the incident record, the Jira ticket and the narrative identify an
// alert by (incident_store.create_incident reads the first twelve).
const ALERT_IDENTITY_KEYS = ['id', 'source', 'timestamp', 'alert_timestamp', 'rule_id', 'rule_description',
  'rule_groups', 'severity', 'severity_num', 'agent_id', 'agent_name', 'source_ip', 'dest_ip', 'src_ip',
  'signature', 'signature_id', 'category', 'integration', 'cve_id', 'package_name', 'package_version',
  'fixed_version', 'http_xff'];

// The last resort: only the identity fields, long text cut to 300 characters
// and lists to 8 items.
function alertIdentity(alert) {
  const out = {};
  for (const k of ALERT_IDENTITY_KEYS) {
    if (alert && alert[k] !== undefined) out[k] = shortenLongValues(alert[k], 300, 8);
  }
  return out;
}

// Ever smaller versions of an alert, most complete first.
function* shrunkAlerts(alert) {
  for (const { maxLen, maxItems } of SHRINK_STEPS) yield shortenLongValues(alert, maxLen, maxItems);
  yield alertIdentity(alert);
}

/**
 * The body, fitted to pass the WAF (fitsWafBudget). Most bodies already fit and
 * are returned unchanged. Otherwise, in order:
 *   shrink: the named field (an alert) is shortened in steps: long text, then
 *           long lists, then only its plain top-level values;
 *   drop:   the named fields are left out, one at a time, for data the server
 *           already holds (the alert it stored at Acknowledge);
 *   trim:   the named text field (the narrative) is cut and marked as
 *           shortened, and `<field>_shortened: true` tells the server to use its
 *           own full copy when it still has one. It is never left out.
 * A body that still does not fit is returned as far as it got.
 */
export async function fitBodyToWafBudget(body, { shrink = null, drop = [], trim = null } = {}) {
  let current = body;
  if (await fitsWafBudget(current)) return current;
  if (shrink && current[shrink] && typeof current[shrink] === 'object') {
    for (const smaller of shrunkAlerts(body[shrink])) {
      current = { ...current, [shrink]: smaller };
      if (await fitsWafBudget(current)) return current;
    }
  }
  for (const key of drop) {
    if (!(key in current)) continue;
    current = { ...current };
    delete current[key];
    if (await fitsWafBudget(current)) return current;
  }
  if (trim && typeof body[trim] === 'string') {
    for (const maxLen of NARRATIVE_STEPS) {
      if (body[trim].length <= maxLen) continue;
      current = { ...current, [trim]: body[trim].slice(0, maxLen) + NARRATIVE_MARK, [`${trim}_shortened`]: true };
      if (await fitsWafBudget(current)) return current;
    }
  }
  return current;
}

/**
 * Splits a list into the fewest requests that each pass the WAF
 * (fitsWafBudget). makeBody(part) builds one request body. Returns the parts in
 * order; an item too large on its own is a part of its own.
 */
export async function splitListForWafBudget(list, makeBody) {
  const parts = [];
  let current = [];
  for (const item of list) {
    const next = [...current, item];
    if (current.length && !(await fitsWafBudget(makeBody(next)))) {
      parts.push(current);
      current = [item];
    } else {
      current = next;
    }
  }
  if (current.length) parts.push(current);
  return parts;
}

/**
 * Splits a list of alerts into the fewest requests that each pass the WAF.
 * makeBody(ids, alertsMap) builds one request body. A single alert too large
 * on its own travels shortened, as fitBodyToWafBudget's shrink does.
 * Returns [{ ids, alerts }], in the original order.
 */
export async function splitAlertsForWafBudget(alertIds, alertsMap, makeBody) {
  const pick = (ids) => {
    const alerts = {};
    for (const id of ids) if (alertsMap[id]) alerts[id] = alertsMap[id];
    return alerts;
  };
  const parts = await splitListForWafBudget(alertIds, (ids) => makeBody(ids, pick(ids)));
  const chunks = parts.map((ids) => ({ ids, alerts: pick(ids) }));
  for (const chunk of chunks) {
    const [only] = chunk.ids;
    if (chunk.ids.length !== 1 || !chunk.alerts[only]) continue;
    if (await fitsWafBudget(makeBody(chunk.ids, chunk.alerts))) continue;
    for (const smaller of shrunkAlerts(alertsMap[only])) {
      chunk.alerts = { [only]: smaller };
      if (await fitsWafBudget(makeBody(chunk.ids, chunk.alerts))) break;
    }
  }
  return chunks;
}

export async function pallasPost(path, body = {}, opts = {}) {
  const { timeoutMs, structuredError } = opts;
  const ctrl = timeoutMs ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;

  try {
    const response = await authedFetch(`${PALLAS_CONFIG.baseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(await packForTransportCompressed(body)),
      signal: ctrl?.signal,
    });
    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      if (response.status === 401) {
        throw new Error('Pallas request was unauthorized. Please contact your Athena administrator.');
      }
      const err = new Error(response.status === 404
        ? `Endpoint ${path} not available. Ensure the MCP server has been updated.`
        : `HTTP ${response.status}: ${errText}`);
      if (structuredError) {
        err.status = response.status;
        try { err.body = errText ? JSON.parse(errText) : null; } catch (_e) { err.body = errText; }
      }
      throw err;
    }
    return response.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// WebSocket Factory
// ---------------------------------------------------------------------------

/**
 * Create a WebSocket connection to the Pallas chat endpoint.
 * Returns the raw WebSocket instance — consumers manage lifecycle.
 *
 * M11 (2026-06-10) - accepts an optional conversationId. When provided,
 * appended as `&conversation_id=<id>` so the backend keys memory under
 * that ID. "New Chat" rotates the ID to start a fresh memory bucket
 * without losing identity-keyed history in OpenSearch.
 */
export async function createChatSocket(conversationId) {
  const ticketResponse = await authedFetch(`${PALLAS_CONFIG.baseUrl}/api/auth/ws-ticket`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!ticketResponse.ok) {
    throw new Error(`WebSocket authentication failed: HTTP ${ticketResponse.status}`);
  }
  const { ticket } = await ticketResponse.json();
  if (!ticket) throw new Error('WebSocket authentication ticket was not returned');

  let url = PALLAS_CONFIG.wsUrl;
  url = `${url}?ticket=${encodeURIComponent(ticket)}`;
  if (conversationId) {
    url = `${url}&conversation_id=${encodeURIComponent(conversationId)}`;
  }
  return new WebSocket(url);
}

// ---------------------------------------------------------------------------
// Tool Metadata (for Tool Runner UI)
// ---------------------------------------------------------------------------

export const PALLAS_TOOL_CATEGORIES = [
  {
    name: 'Alerts & Events',
    tools: [
      'get_wazuh_alerts',
      'get_wazuh_alert_summary',
      'analyze_alert_patterns',
      'search_security_events',
      'get_top_security_threats',
    ],
  },
  {
    name: 'Agents',
    tools: [
      'get_wazuh_agents',
      'get_wazuh_running_agents',
      'check_agent_health',
      'get_agent_processes',
      'get_agent_ports',
      'get_agent_configuration',
    ],
  },
  {
    name: 'Vulnerabilities',
    tools: [
      'get_wazuh_vulnerabilities',
      'get_wazuh_critical_vulnerabilities',
      'get_wazuh_vulnerability_summary',
    ],
  },
  {
    name: 'Intelligence & Compliance',
    tools: [
      'analyze_security_threat',
      'check_ioc_reputation',
      'perform_risk_assessment',
      'generate_security_report',
      'run_compliance_check',
    ],
  },
  {
    name: 'System & Cluster',
    tools: [
      'get_wazuh_statistics',
      'get_wazuh_weekly_stats',
      'get_wazuh_cluster_health',
      'get_wazuh_cluster_nodes',
      'get_wazuh_rules_summary',
      'get_wazuh_remoted_stats',
      'get_wazuh_log_collector_stats',
      'search_wazuh_manager_logs',
      'get_wazuh_manager_error_logs',
      'validate_wazuh_connection',
    ],
  },
];
